<script setup lang="ts">
import { ref, nextTick, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { VAppBar } from 'vuetify/components'
import useAccountStore from '@/stores/modules/account'
import useGlobalStore from '@/stores/modules/global'
import {
  DEFAULT_APP_BAR_HEIGHT,
  EMPLOYEES_URL,
  TEMPLATE_ADD_URL_NAME
} from '@/utils/constants'
import NotificationType from '@/enum/NotificationType'
import CONSTANTS from '@/utils/constants/string-constants'
import { BaseButton, BaseMenu } from '@/components/base'
import ConfirmationDialogBox from './dialog-boxes/ConfirmationDialogBox.vue'
import UserAvatar from './UserAvatar.vue'
import AppBreadcrumbs from './AppBreadcrumbs.vue'
import { NotificationIcon } from '@/assets'

defineProps<{
  navbarCollapsed: boolean
}>()

const route = useRoute()
const accountStore = useAccountStore()
const globalStore = useGlobalStore()
const appBar = ref()
const highlight = ref({ width: 0, left: 0 })
const logoutConfirmation = ref(false)
const profile = computed(() => accountStore.profile)
const hasPermissionsAndProfile = computed(() => accountStore.hasProfileInfo)
const navbarMenuOptions = computed(() => [
  {
    icon: 'mdi:mdi-account',
    label: CONSTANTS.PROFILE,
    path: `${EMPLOYEES_URL}/${accountStore.profile?.id}/edit`,
    color: 'black'
  },
  {
    icon: 'mdi:mdi-logout',
    label: CONSTANTS.LOGOUT,
    action: () => {
      logoutConfirmation.value = true
    },
    color: 'error'
  }
])

const computedRouteName = computed(() => {
  switch (route.name) {
    case TEMPLATE_ADD_URL_NAME:
      return CONSTANTS.TEMPLATE_ADD_APP_BAR_TITLE
    default:
      return route.name
  }
})

const isNestedRoute = computed(() => {
  if (route.path.split('/').length > 2) return true
  return false
})

const logoutUserAndResetState = () => {
  logoutConfirmation.value = false
  const message = 'Logging out, please wait'
  globalStore.addToastNotification({
    id: Symbol('USER_LOGOUT'),
    type: NotificationType.Info,
    message
  })
  accountStore.logoutUser()
}

const moveCurrentRouteHighlight = () => {
  nextTick(() => {
    const { path, params, fullPath } = route
    const navbarLinks = document.querySelectorAll('header [href]')
    const currentRoute = Array.from(navbarLinks).find((link) =>
      link.getAttribute('href')?.includes(path.split('/')[1])
    )
    const mainRoute = currentRoute?.getAttribute('href')?.split('/')[1]
    let trackerElement = document.querySelector('.route-highlight__tracker') as HTMLElement
    if (path.split('/').length > 2 || Object.keys(params).length || fullPath.includes('?')) {
      trackerElement = document.querySelector(`[href*="${mainRoute}"]`) as HTMLElement
    }
    highlight.value = { width: trackerElement?.offsetWidth, left: trackerElement?.offsetLeft }
  })
}

const clearBreadcrumbs = () => {
  if (globalStore.breadcrumbs.length) globalStore.clearBreadcrumbs()
}

const handleNotificationsListVisibility = () => {
  globalStore.toggleNotificationsFlyout()
  globalStore.hasNewNotifications = false
}

watch(
  route,
  () => {
    moveCurrentRouteHighlight()
    clearBreadcrumbs()
  },
  { flush: 'post' }
)
watch(() => hasPermissionsAndProfile.value, moveCurrentRouteHighlight, { flush: 'post' })

onMounted(() => {
  const timeoutID = setTimeout(() => {
    moveCurrentRouteHighlight()
    clearTimeout(timeoutID)
  })
})
</script>
<template>
  <v-app-bar
    ref="appBar"
    :height="DEFAULT_APP_BAR_HEIGHT"
    class="main-container--padding app-bar flex-grow-0 position-sticky"
    app
    color="white"
    :navbar-collapsed="navbarCollapsed"
  >
    <div class="d-flex align-center ga-3 ml-6 w-100">
      <h1 v-if="!isNestedRoute" class="main-header semibold">{{ computedRouteName }}</h1>
      <AppBreadcrumbs v-else />
    </div>
    <v-spacer />
    <div class="d-flex align-center ga-3">
      <BaseButton
        class="mr-3 pa-0 notification-icon bg-off_white rounded-circle d-flex align-center justify-center rounded-circle"
        min-width="unset"
        style="height: 40px !important; width: 40px !important"
        :has-notifications="globalStore.hasNewNotifications"
        @click="handleNotificationsListVisibility"
      >
        <v-img :src="NotificationIcon" width="20" height="20" />
      </BaseButton>
      <UserAvatar :loading="!profile" :name="profile?.name" :icon-size="58" />
      <v-skeleton-loader v-if="!profile" type="text" width="100" class="mt-2" />
      <div v-else class="d-flex align-center ga-3">
        <div class="d-flex flex-column align-start">
          <span class="medium body-1 text-capitalize">
            {{ profile?.userDisplayName }}
          </span>
          <span class="body-2">{{ profile.email }}</span>
        </div>
        <BaseMenu
          :items="navbarMenuOptions"
          tile
          icon-position="left"
          content-class="white--text app-bar--menu"
          transition="slide-y-transition"
        >
          <template #menu-activator>
            <span class="account-menu-options d-block bg-off_white rounded-circle">
              <v-icon icon="mdi:mdi-chevron-down" size="24" />
            </span>
          </template>
        </BaseMenu>
      </div>
    </div>
    <ConfirmationDialogBox
      v-model="logoutConfirmation"
      width="360"
      :confirmation-button-text="CONSTANTS.LOGOUT"
      @cancel="() => (logoutConfirmation = false)"
      @confirm="logoutUserAndResetState"
    >
      <template #confirmation-title>{{ CONSTANTS.LOGOUT }}</template>
      <template #confirmation-message>{{ CONSTANTS.LOGOUT_MESSAGE }}</template>
    </ConfirmationDialogBox>
    <span
      :class="`highlight-route ${!hasPermissionsAndProfile ? 'loading' : ''}`"
      :style="
        hasPermissionsAndProfile
          ? { width: highlight?.width + 'px', left: highlight?.left + 'px' }
          : ''
      "
    />
  </v-app-bar>
</template>
<style scoped lang="scss">
.app-bar {
  z-index: 4;
  width: 100% !important;
  box-shadow: 0px 1px 4px 0px #0000000d !important;
}

.route-highlight__tracker {
  position: relative;
}
.route-highlight__tracker::after {
  content: '';
  display: block;
  position: absolute;
  height: 4px;
  left: 0;
  right: 0;
  bottom: 0;
}
.highlight-route {
  display: block;
  position: absolute;
  height: 4px;
  background-color: var(--v-brand_light-base);
  bottom: 0;
  transition: all 0.3s cubic-bezier(0.87, 0, 0.13, 1);
}

.app-bar--menu {
  box-shadow: none;
}

.app-bar--menu .v-list {
  margin-block: 6px;
  border: 1px solid rgb(var(--v-theme-blue_500));
}

.app-bar--menu .v-list::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: rotateX(180deg) translateY(50%);
  margin-left: 32px;
  border-width: 6px;
  border-style: solid;
  border-color: var(--v-black-base) transparent transparent transparent;
}
.app-version {
  font-size: 12px;
  color: #696969;
  margin-top: 1px;
  line-height: 18px;
}
.router-block:empty {
  padding: 0;
}
.highlight-route.loading {
  animation-name: loadingPermissions;
  animation-duration: 5s;
  animation-iteration-count: 1;
  animation-timing-function: cubic-bezier(0.075, 0.82, 0.165, 1);
  animation-fill-mode: forwards;
  left: 0;

  @keyframes loadingPermissions {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }
}

.app-bar--menu__item {
  padding: 24px 0px;
}

.app-bar-logo-container {
  height: 40px;
  width: 40px;
  background: var(--v-brand_dark-base);
  text-align: center;
  padding-top: 4px;
}

.app-bar-logo-label {
  background-color: #fff;
  color: var(--v-brand_dark-base);
  text-align: center;
  vertical-align: middle;
  font-weight: 600;
  font-size: 16px;
  line-height: 22.6px;
  width: 86px;
  height: 40px;
  border: 1px solid var(--v-brand_dark-base);

  &-text {
    padding-top: 9px;
    text-rendering: optimizeLegibility;
  }
}

.position-sticky {
  position: sticky !important;
}
.notification-icon {
  width: 48px;
  height: 40px;
  &[has-notifications='true']::before {
    content: '';
    display: block;
    height: 8px;
    width: 8px;
    position: absolute;
    top: 4px;
    right: 0;
    transform: translate(-50%, -50%);
    background-color: rgb(var(--v-theme-orange_600));
    border-radius: 50%;
  }
}

.account-menu-options {
  width: 24px;
  height: 24px;
}
</style>
